'use client';

/**
 * AI assistant open/close state ("Valiyou" in-app assistant).
 *
 * Kept as a tiny context so the orb (opens) and the panel (closes) can live
 * in different parts of the layout tree. Chat state itself lives inside the
 * panel component, which stays mounted across route changes.
 */

import { createContext, useContext, useState, useMemo, type ReactNode } from 'react';

interface AiAssistantContextType {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /** True while the AI is busy (streaming or executing client tools). */
  working: boolean;
  setWorking: (working: boolean) => void;
}

const AiAssistantContext = createContext<AiAssistantContextType | undefined>(undefined);

export function AiAssistantProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [working, setWorking] = useState(false);

  const value = useMemo(
    () => ({
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      working,
      setWorking,
    }),
    [isOpen, working]
  );

  return <AiAssistantContext.Provider value={value}>{children}</AiAssistantContext.Provider>;
}

export function useAiAssistant() {
  const context = useContext(AiAssistantContext);
  if (context === undefined) {
    throw new Error('useAiAssistant must be used within an AiAssistantProvider');
  }
  return context;
}
